import * as React from 'react';
import { useEffect } from 'react';

/*
 * Form & feedback vocabulary, built to the Cursor Agents kit's own
 * recipes (docs/design-brief/cursor-agents-ui-kit.md): text input
 * §8.15, buttons + danger tokens §8.5/§2.3, status badge §8.6,
 * dialog grammar §8.9/§8.11, toast §8.12, empty panel §8.14.
 * The settings-form archetype (~10 screens) instantiates these.
 */

export function Field({
    label,
    help,
    error,
    children,
}: {
    label: string;
    help?: string;
    error?: string;
    children: React.ReactNode;
}) {
    return (
        <label className="field">
            <span className="field-l">{label}</span>
            {children}
            {error ? (
                <span className="field-e">{error}</span>
            ) : (
                help && <span className="field-h">{help}</span>
            )}
        </label>
    );
}

export function TextInput(
    props: React.InputHTMLAttributes<HTMLInputElement>,
) {
    return (
        <input
            className="input"
            spellCheck={false}
            autoComplete="off"
            {...props}
        />
    );
}

export function Textarea(
    props: React.TextareaHTMLAttributes<HTMLTextAreaElement>,
) {
    return <textarea className="input textarea" spellCheck={false} {...props} />;
}

export function Select({
    children,
    ...props
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
    return (
        <span className="selectwrap">
            <select className="input select" {...props}>
                {children}
            </select>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                <path d="m6 9 6 6 6-6" />
            </svg>
        </span>
    );
}

export function Checkbox({
    label,
    ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
    return (
        <label className="check">
            <input type="checkbox" {...props} />
            <span className="box" aria-hidden="true">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="m5 12 4 4L19 6" />
                </svg>
            </span>
            {label}
        </label>
    );
}

export function Toggle({
    label,
    ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
    return (
        <label className="check">
            <input type="checkbox" role="switch" {...props} />
            <span className="knobtrack" aria-hidden="true">
                <span className="knob" />
            </span>
            {label}
        </label>
    );
}

export function Dialog({
    open,
    title,
    onClose,
    onSubmit,
    footer,
    children,
}: {
    open: boolean;
    title: string;
    onClose: () => void;
    /** when set, body+footer render inside a <form> so Enter submits */
    onSubmit?: () => void;
    footer?: React.ReactNode;
    children: React.ReactNode;
}) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);
    if (!open) return null;
    /* rendered in place (inside the .psv3 scope) — position:fixed still
       covers the viewport, and the kit tokens stay inherited */
    return (
        <div className="psv3-scrim" onMouseDown={onClose}>
            <div
                className="dialog"
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="dialog-h">{title}</div>
                {onSubmit ? (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            onSubmit();
                        }}
                    >
                        <div className="dialog-b">{children}</div>
                        {footer && <div className="dialog-f">{footer}</div>}
                    </form>
                ) : (
                    <>
                        <div className="dialog-b">{children}</div>
                        {footer && <div className="dialog-f">{footer}</div>}
                    </>
                )}
            </div>
        </div>
    );
}

/** The destructive confirm (archive is soft and needs none; delete and
    revoke do): plain sentence, outline cancel, solid danger action. */
export function ConfirmDialog({
    open,
    title,
    body,
    actionLabel,
    onConfirm,
    onClose,
    danger = true,
}: {
    open: boolean;
    title: string;
    body: string;
    actionLabel: string;
    onConfirm: () => void;
    onClose: () => void;
    /** red action button (default); false for a plain confirm */
    danger?: boolean;
}) {
    return (
        <Dialog
            open={open}
            title={title}
            onClose={onClose}
            footer={
                <>
                    <button className="tb-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className={danger ? 'tb-new danger' : 'tb-new'}
                        onClick={() => {
                            onConfirm();
                            onClose();
                        }}
                    >
                        {actionLabel}
                    </button>
                </>
            }
        >
            <p style={{ margin: 0 }}>{body}</p>
        </Dialog>
    );
}


/* ---- settings-form archetype pieces (sharing-concept.html) ---- */

export function Crumb({
    label,
    onClick,
}: {
    label: string;
    onClick: () => void;
}) {
    return (
        <button className="crumb" onClick={onClick}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M19 12H5m0 0 6 6m-6-6 6-6" />
            </svg>
            {label}
        </button>
    );
}

export function SectionCard({
    title,
    note,
    action,
    children,
}: {
    title: string;
    note?: string;
    action?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <div className="sec">
            <div className="sec-h">
                <h2>{title}</h2>
                {note && <span className="n">{note}</span>}
                {action && <span className="act">{action}</span>}
            </div>
            {children}
        </div>
    );
}

/** A row in an all-immediate section: flipping it saves it (rule 51). */
export function ToggleRow({
    label,
    sub,
    checked,
    disabled,
    onChange,
}: {
    label: string;
    sub?: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (on: boolean) => void;
}) {
    return (
        <div className="mrow-t">
            <span>
                <span className="lbl">{label}</span>
                {sub && (
                    <>
                        <br />
                        <span className="sub3">{sub}</span>
                    </>
                )}
            </span>
            <span className="tg">
                <Toggle
                    label=""
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.checked)}
                />
            </span>
        </div>
    );
}

/** A row in a batched section: the checkbox rides the save bar. */
export function CheckRow({
    label,
    checked,
    onChange,
}: {
    label: string;
    checked: boolean;
    onChange: (on: boolean) => void;
}) {
    return (
        <div className="mrow-t">
            <span className="lbl">{label}</span>
            <span className="tg">
                <Checkbox
                    label=""
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                />
            </span>
        </div>
    );
}

/** Appears when a batched section is dirty; names what's unsaved. */
export function SaveBar({
    dirty,
    saving,
    onSave,
    onDiscard,
}: {
    /** dirty section names; empty = hidden */
    dirty: string[];
    saving?: boolean;
    onSave: () => void;
    onDiscard: () => void;
}) {
    if (dirty.length === 0) return null;
    return (
        <div className="savebar">
            <span className="msg">
                Unsaved changes in {dirty.join(' · ')}
            </span>
            <span className="sp" />
            <button className="tb-btn ghost" onClick={onDiscard}>
                Discard
            </button>
            <button
                className="tb-new"
                disabled={saving}
                onClick={onSave}
            >
                {saving ? 'Saving…' : 'Save changes'}
            </button>
        </div>
    );
}
